import { useMemo } from 'react';
import { getMonthYear } from './useExecutiveDashboardData';

// ----- Category List Constants -----
export const incomeList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Insurance Income - Revenue",
    "Other Income",
    "Other Contractual Income",
    "Promotional Income",
];

export const netMunicipalsList = [
    "Rates Income",
    "Electricity Recovered",
    "Water Recovered",
    "Sewerage Recovered ",
    "Refuse Recovered",
    "Rates Expense",
    "Electricity Expense",
    "Water Consumption Expense",
    "Sewerage Expense",
    "Refuse Expense"
];

export const opExList = [
    "Bank Charges",
    "Cleaning",
    "Management Fee",
    "Marketing",
    "Other Operating Expenses",
    "Repairs & Maintenance",
    "Sanitation & Pest Control",
    "Security",
];

export const fuelExpenseList = ["Fuel"];

export const nonOpExList = [
    "Audit Secretarial Fees",
    "Professional Fees",
    "SRI Expenses",
    "Depreciation"
];

export const netInterestExpenseList = [
    "Interest Received",
    "Interest Paid",
    "Interest Paid InterCo"
];

export const revalTaxList = [
    "Revaluation",
    "Profit/ Loss associate",
    "Taxation"
];

export const badDebtsList = [
    "Provision for Bad Debts",
    "Bad debts written off",
    "Bad Debts Recovered"
];

export const billingsList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Other Contractual Income",
    "Promotional Income",
    "Rates Income",
    "Electricity Recovered",
    "Water Recovered",
    "Sewerage Recovered ",
    "Refuse Recovered"
];

export const utilitiesIncomeList = [
    "Electricity Recovered",
    "Water Recovered",
    "Sewerage Recovered ",
    "Refuse Recovered"
];

export const utilitiesExpenseList = [
    "Electricity Expense",
    "Water Consumption Expense",
    "Sewerage Expense",
    "Refuse Expense"
];

export const lettingCostList = [
    "Tenant Installations",
    "Consolidation Costs",
    "Letting Commission"
];

export const combinedOpExAndFuelList = [...opExList, ...fuelExpenseList];

export function _parseIncomeValue (val) {
    if (typeof val === "number") return val;
    if (typeof val === "string") {
        // Remove spaces, commas, and handle "-" as 0
        const cleaned = val.replace(/[\s,']/g, '');
        if (cleaned === "-" || cleaned === "") return 0;
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
    }
    return 0;
};

export function _formatNumberWithSpace (value) {
    if (typeof value !== 'number') return value;
    // Format with fr-FR to get the grouping
    let formatted = value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
    return formatted.replace(/\u202F/g, '  ');
};

export function calculateTotalIncomeForCaptions(includedCaptions, valueProperty, data) {
    if (!data || !includedCaptions) return 0;

    const total = data.reduce((sum, item) => {
        if (includedCaptions.includes(item.Caption)) {

            const value = typeof item[valueProperty] === 'number'
                ? item[valueProperty]
                : parseFloat(item[valueProperty] || '0');

            return sum + (value * -1);
        }
        return sum;
    }, 0);

    return total;
}  

export function getTotalForTransactionCode(transactionCode, valueProperty, data) {
    if (!data) return 0;

    return data
        .filter(item => item.TransactionCode === transactionCode)
        .reduce((sum, item) => {
            const value = typeof item[valueProperty] === 'number'
                ? item[valueProperty]
                : parseFloat(item[valueProperty] || '0');
            return sum + value;
        }, 0);
}

export function sumCaptionsToMillions(captionList, valueField, data){
    if (!data) return "-";

    const total = calculateTotalIncomeForCaptions(captionList, valueField, data);
    const inMillions = total / 1000000;
    return inMillions.toLocaleString('en-ZA', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })
};

const useExecutiveDashboardCalculations = (condensedIncomeData, condensedIncomeDataPrev, period, totalArea, totalOccupiedGLA) => {

    // Average Occupied Rate Calculations
    const averageRateOccupiedGLA = useMemo(() => {
        if (!condensedIncomeData || !totalOccupiedGLA || totalOccupiedGLA === 0) return "-";

        const captions = ["Basic Contractual Income", "Rates Income"];
        const total = calculateTotalIncomeForCaptions(captions, 'CurrentMovements', condensedIncomeData);
        const rate = total / totalOccupiedGLA;

        return Math.round(rate).toLocaleString('en-ZA', { useGrouping: true, maximumFractionDigits: 0 });
    }, [condensedIncomeData, totalOccupiedGLA]);

    const averageRateOccupiedGLAYtd = useMemo(() => {
        if (!condensedIncomeData || !totalOccupiedGLA || totalOccupiedGLA === 0 || !period) return "-";

        const captions = ["Basic Contractual Income", "Rates Income"];
        const total = calculateTotalIncomeForCaptions(captions, 'CurrentBal', condensedIncomeData);

        const { monthNum } = getMonthYear(period);
        const rate = (total / monthNum) / totalOccupiedGLA;

        return Math.round(rate).toLocaleString('en-ZA', { useGrouping: true, maximumFractionDigits: 0 });
    }, [condensedIncomeData, totalOccupiedGLA, period]);

    const averageRateOccupiedGLAPrev = useMemo(() => {
        if (!condensedIncomeDataPrev || !totalOccupiedGLA || totalOccupiedGLA === 0) return "-";

        const captions = ["Basic Contractual Income", "Rates Income"];
        const total = calculateTotalIncomeForCaptions(captions, 'CurrentBal', condensedIncomeDataPrev);
        const rate = (total / 12) / totalOccupiedGLA;

        return Math.round(rate).toLocaleString('en-ZA', { useGrouping: true, maximumFractionDigits: 0 });
    }, [condensedIncomeDataPrev, totalOccupiedGLA]);

    // Average Rate Calculations
    const averageRateGLA = useMemo(() => {
        if (!condensedIncomeData || !totalArea || totalArea === 0) return "-";

        const captions = ["Basic Contractual Income", "Rates Income"];
        const total = calculateTotalIncomeForCaptions(captions, 'CurrentMovements', condensedIncomeData);
        const rate = total / totalArea;

        return Math.round(rate).toLocaleString('en-ZA', { useGrouping: true, maximumFractionDigits: 0 });
    }, [condensedIncomeData, totalArea]);

    const averageRateGLAYtd = useMemo(() => {
        if (!condensedIncomeData || !totalArea || totalArea === 0 || !period) return "-";

        const captions = ["Basic Contractual Income", "Rates Income"];
        const total = calculateTotalIncomeForCaptions(captions, 'CurrentBal', condensedIncomeData);

        const { monthNum } = getMonthYear(period);
        const rate = (total / monthNum) / totalArea;

        return Math.round(rate).toLocaleString('en-ZA', { useGrouping: true, maximumFractionDigits: 0 });
    }, [condensedIncomeData, totalArea, period]);

    const averageRateGLAPrev = useMemo(() => {
        if (!condensedIncomeDataPrev || !totalArea || totalArea === 0) return "-";

        const captions = ["Basic Contractual Income", "Rates Income"];
        const total = calculateTotalIncomeForCaptions(captions, 'CurrentBal', condensedIncomeDataPrev);
        const rate = (total / 12) / totalArea;

        return Math.round(rate).toLocaleString('en-ZA', { useGrouping: true, maximumFractionDigits: 0 });
    }, [condensedIncomeDataPrev, totalArea]);

    //Recovery Rate Calculations
    const recoveryRates = useMemo(() => {
        if (!condensedIncomeData || !condensedIncomeDataPrev) return { month: "-", ytd: "-", fullYear: "-" };

        const ratesIncomeMonth = calculateTotalIncomeForCaptions(["Rates Income"], "CurrentMovements", condensedIncomeData);
        const ratesExpenseMonth = calculateTotalIncomeForCaptions(["Rates Expense"], "CurrentMovements", condensedIncomeData);
        const month = ratesExpenseMonth !== 0
            ? ((ratesIncomeMonth / -ratesExpenseMonth) * 100).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
            : "-";

        const ratesIncomeYTD = calculateTotalIncomeForCaptions(["Rates Income"], "CurrentBal", condensedIncomeData);
        const ratesExpenseYTD = calculateTotalIncomeForCaptions(["Rates Expense"], "CurrentBal", condensedIncomeData);
        const ytd = ratesExpenseYTD !== 0
            ? ((ratesIncomeYTD / -ratesExpenseYTD) * 100).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
            : "-";

        const ratesIncomePrev = calculateTotalIncomeForCaptions(["Rates Income"], "CurrentBal", condensedIncomeDataPrev);
        const ratesExpensePrev = calculateTotalIncomeForCaptions(["Rates Expense"], "CurrentBal", condensedIncomeDataPrev);
        const fullYear = ratesExpensePrev !== 0
            ? ((ratesIncomePrev / -ratesExpensePrev) * 100).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
            : "-";

        return { month, ytd, fullYear };
    }, [condensedIncomeData, condensedIncomeDataPrev]);

    const recoveryRatioUtilities = useMemo(() => {
        if (!condensedIncomeData || !condensedIncomeDataPrev) return { month: "-", ytd: "-", fullYear: "-" };

        const utilitiesIncomeMonth = calculateTotalIncomeForCaptions(utilitiesIncomeList, "CurrentMovements", condensedIncomeData);
        const utilitiesExpenseMonth = calculateTotalIncomeForCaptions(utilitiesExpenseList, "CurrentMovements", condensedIncomeData);
        const month = utilitiesExpenseMonth !== 0
            ? ((utilitiesIncomeMonth / -utilitiesExpenseMonth) * 100).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
            : "-";

        const utilitiesIncomeYTD = calculateTotalIncomeForCaptions(utilitiesIncomeList, "CurrentBal", condensedIncomeData);
        const utilitiesExpenseYTD = calculateTotalIncomeForCaptions(utilitiesExpenseList, "CurrentBal", condensedIncomeData);
        const ytd = utilitiesExpenseYTD !== 0
            ? ((utilitiesIncomeYTD / -utilitiesExpenseYTD) * 100).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
            : "-";

        const utilitiesIncomePrev = calculateTotalIncomeForCaptions(utilitiesIncomeList, "CurrentBal", condensedIncomeDataPrev);
        const utilitiesExpensePrev = calculateTotalIncomeForCaptions(utilitiesExpenseList, "CurrentBal", condensedIncomeDataPrev);
        const fullYear = utilitiesExpensePrev !== 0
            ? ((utilitiesIncomePrev / -utilitiesExpensePrev) * 100).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
            : "-";

        return { month, ytd, fullYear };
    }, [condensedIncomeData, condensedIncomeDataPrev]);

    const expenseRatioCurrent = useMemo(() => {
        if (!condensedIncomeData) return "-";

        const operatingExpenses = calculateTotalIncomeForCaptions(combinedOpExAndFuelList, "CurrentMovements", condensedIncomeData);
        const insuranceExpenses = -(getTotalForTransactionCode(323, "CurrentMovements", condensedIncomeData));
        const legalFeesExpenses = -(getTotalForTransactionCode(326, "CurrentMovements", condensedIncomeData));
        const totalOpEx = operatingExpenses + insuranceExpenses + legalFeesExpenses;

        const netMunicipal = calculateTotalIncomeForCaptions(netMunicipalsList, "CurrentMovements", condensedIncomeData);
        const badDebts = calculateTotalIncomeForCaptions(badDebtsList, "CurrentMovements", condensedIncomeData);
        const lettingCosts = calculateTotalIncomeForCaptions(lettingCostList, "CurrentMovements", condensedIncomeData);
        const fundExpenses = calculateTotalIncomeForCaptions(nonOpExList, "CurrentMovements", condensedIncomeData);
        const income = calculateTotalIncomeForCaptions(incomeList, "CurrentMovements", condensedIncomeData);

        const total = (- (netMunicipal + totalOpEx + badDebts + lettingCosts + fundExpenses) / income) * 100;

        return total.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }) + '%';
    }, [condensedIncomeData]);

    const expenseRatioYTD = useMemo(() => {
        if (!condensedIncomeData) return "-";

        const operatingExpenses = calculateTotalIncomeForCaptions(combinedOpExAndFuelList, "CurrentBal", condensedIncomeData);
        const insuranceExpenses = -(getTotalForTransactionCode(323, "CurrentBal", condensedIncomeData));
        const legalFeesExpenses = -(getTotalForTransactionCode(326, "CurrentBal", condensedIncomeData));
        const totalOpEx = operatingExpenses + insuranceExpenses + legalFeesExpenses;

        const netMunicipal = calculateTotalIncomeForCaptions(netMunicipalsList, "CurrentBal", condensedIncomeData);
        const badDebts = calculateTotalIncomeForCaptions(badDebtsList, "CurrentBal", condensedIncomeData);
        const lettingCosts = calculateTotalIncomeForCaptions(lettingCostList, "CurrentBal", condensedIncomeData);
        const fundExpenses = calculateTotalIncomeForCaptions(nonOpExList, "CurrentBal", condensedIncomeData);
        const income = calculateTotalIncomeForCaptions(incomeList, "CurrentBal", condensedIncomeData);

        const total = (- (netMunicipal + totalOpEx + badDebts + lettingCosts + fundExpenses) / income) * 100;

        return total.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }) + '%';
    }, [condensedIncomeData]);

    const expenseRatioPrev = useMemo(() => {
        if (!condensedIncomeDataPrev) return "-";

        const operatingExpenses = calculateTotalIncomeForCaptions(combinedOpExAndFuelList, "CurrentBal", condensedIncomeDataPrev);
        const insuranceExpenses = -(getTotalForTransactionCode(323, "CurrentBal", condensedIncomeDataPrev));
        const legalFeesExpenses = -(getTotalForTransactionCode(326, "CurrentBal", condensedIncomeDataPrev));
        const totalOpEx = operatingExpenses + insuranceExpenses + legalFeesExpenses;

        const netMunicipal = calculateTotalIncomeForCaptions(netMunicipalsList, "CurrentBal", condensedIncomeDataPrev);
        const badDebts = calculateTotalIncomeForCaptions(badDebtsList, "CurrentBal", condensedIncomeDataPrev);
        const lettingCosts = calculateTotalIncomeForCaptions(lettingCostList, "CurrentBal", condensedIncomeDataPrev);
        const fundExpenses = calculateTotalIncomeForCaptions(nonOpExList, "CurrentBal", condensedIncomeDataPrev);
        const income = calculateTotalIncomeForCaptions(incomeList, "CurrentBal", condensedIncomeDataPrev);

        const total = (- (netMunicipal + totalOpEx + badDebts + lettingCosts + fundExpenses) / income) * 100;

        return total.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }) + '%';
    }, [condensedIncomeDataPrev]);

    const propertyExpenseRatioMonth = useMemo(() => {
        if (!condensedIncomeData) return "-";

        const operatingExpenses = calculateTotalIncomeForCaptions(combinedOpExAndFuelList, "CurrentMovements", condensedIncomeData);
        const insuranceExpenses = -(getTotalForTransactionCode(323, "CurrentMovements", condensedIncomeData));
        const legalFeesExpenses = -(getTotalForTransactionCode(326, "CurrentMovements", condensedIncomeData));
        const totalOpEx = operatingExpenses + insuranceExpenses + legalFeesExpenses;

        const value2 = getTotalForTransactionCode(399, "CurrentBal", condensedIncomeData);
        const value3 = getTotalForTransactionCode(412, "CurrentBal", condensedIncomeData);
        const value4 = calculateTotalIncomeForCaptions(netMunicipalsList, "CurrentMovements", condensedIncomeData);
        const mediaIncome = -(getTotalForTransactionCode(104, "CurrentMovements", condensedIncomeData));
        const incomeTotal = calculateTotalIncomeForCaptions(incomeList, "CurrentMovements", condensedIncomeData);
        const finalIncome = incomeTotal + mediaIncome;

        if (finalIncome === 0) return "-";

        const numerator = totalOpEx + value2 + value3 + value4;
        const ratio = -(numerator / finalIncome) * 100;
        return ratio.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
    }, [condensedIncomeData]);

    const propertyExpenseRatioYTD = useMemo(() => {
        if (!condensedIncomeData) return "-";

        const operatingExpenses = calculateTotalIncomeForCaptions(combinedOpExAndFuelList, "CurrentBal", condensedIncomeData);
        const insuranceExpenses = -(getTotalForTransactionCode(323, "CurrentBal", condensedIncomeData));
        const legalFeesExpenses = -(getTotalForTransactionCode(326, "CurrentBal", condensedIncomeData));
        const totalOpEx = operatingExpenses + insuranceExpenses + legalFeesExpenses;

        const value2 = getTotalForTransactionCode(399, "CurrentBal", condensedIncomeData);
        const value3 = getTotalForTransactionCode(412, "CurrentBal", condensedIncomeData);
        const value4 = calculateTotalIncomeForCaptions(netMunicipalsList, "CurrentBal", condensedIncomeData);
        const incomeTotal = calculateTotalIncomeForCaptions(incomeList, "CurrentBal", condensedIncomeData);

        if (incomeTotal === 0) return "-";

        const numerator = totalOpEx + value2 + value3 + value4;
        const ratio = -(numerator / incomeTotal) * 100;
        return ratio.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
    }, [condensedIncomeData]);

    const propertyExpenseRatioPrev = useMemo(() => {
        if (!condensedIncomeDataPrev) return "-";

        const operatingExpenses = calculateTotalIncomeForCaptions(combinedOpExAndFuelList, "CurrentBal", condensedIncomeDataPrev);
        const insuranceExpenses = -(getTotalForTransactionCode(323, "CurrentBal", condensedIncomeDataPrev));
        const legalFeesExpenses = -(getTotalForTransactionCode(326, "CurrentBal", condensedIncomeDataPrev));
        const totalOpEx = operatingExpenses + insuranceExpenses + legalFeesExpenses;

        const value2 = getTotalForTransactionCode(399, "CurrentBal", condensedIncomeDataPrev);
        const value3 = getTotalForTransactionCode(412, "CurrentBal", condensedIncomeDataPrev);
        const value4 = calculateTotalIncomeForCaptions(netMunicipalsList, "CurrentBal", condensedIncomeDataPrev);
        const incomeTotal = calculateTotalIncomeForCaptions(incomeList, "CurrentBal", condensedIncomeDataPrev);

        if (incomeTotal === 0) return "-";

        const numerator = totalOpEx + value2 + value3 + value4;
        const ratio = -(numerator / incomeTotal) * 100;
        return ratio.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
    }, [condensedIncomeDataPrev]);

    const badDebts = useMemo(() => {
        if (!condensedIncomeData || !condensedIncomeDataPrev) return { percentageCurrent: "-", percentageYTD: "-", percentagePrev: "-" };;

        //Month
        const badDebtsMonth = calculateTotalIncomeForCaptions(badDebtsList, "CurrentMovements", condensedIncomeData);
        const totalIncomeMonth = calculateTotalIncomeForCaptions(incomeList, "CurrentMovements", condensedIncomeData);
        const finalRatioMonth = - badDebtsMonth / totalIncomeMonth;
        const percentageCurrent = (finalRatioMonth * 100).toLocaleString("en-ZA", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";

        //YTD
        const badDebtsYTD = calculateTotalIncomeForCaptions(badDebtsList, "CurrentBal", condensedIncomeData);
        const totalIncomeYTD = calculateTotalIncomeForCaptions(incomeList, "CurrentBal", condensedIncomeData);
        const finalRatioYTD = - badDebtsYTD / totalIncomeYTD;
        const percentageYTD = (finalRatioYTD * 100).toLocaleString("en-ZA", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";

        //Prev Year
        const badDebtsPrev = calculateTotalIncomeForCaptions(badDebtsList, "BudgetFullYearRevised", condensedIncomeDataPrev);
        const totalIncomePrev = calculateTotalIncomeForCaptions(incomeList, "BudgetFullYearRevised", condensedIncomeDataPrev);
        const finalRatioPrev = - badDebtsPrev / totalIncomePrev;
        const percentagePrev = (finalRatioPrev * 100).toLocaleString("en-ZA", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";

        return { percentageCurrent, percentageYTD, percentagePrev };
    }, [condensedIncomeData, condensedIncomeDataPrev]);

    const billings = useMemo(() => {
        if (!condensedIncomeData || !period) return { monthlyBillingsCurrent: 0, monthlyBillingsYTD: 0, monthlyBillingsPrev: 0 };

        const { monthNum } = getMonthYear(period);

        const monthlyBillingsCurrent = calculateTotalIncomeForCaptions(billingsList, "CurrentMovements", condensedIncomeData);
        const monthlyBillingsYTD = calculateTotalIncomeForCaptions(billingsList, "CurrentBal", condensedIncomeData) / monthNum;
        const monthlyBillingsPrev = condensedIncomeDataPrev
            ? calculateTotalIncomeForCaptions(billingsList, "CurrentBal", condensedIncomeDataPrev) / 12
            : 0;

        return { monthlyBillingsCurrent, monthlyBillingsYTD, monthlyBillingsPrev };
    }, [condensedIncomeData, condensedIncomeDataPrev, period]);

    return {
        averageRateOccupiedGLA,
        averageRateOccupiedGLAYtd,
        averageRateOccupiedGLAPrev,
        averageRateGLA,
        averageRateGLAYtd,
        averageRateGLAPrev,
        recoveryRates,
        recoveryRatioUtilities,
        expenseRatioCurrent,
        expenseRatioYTD,
        expenseRatioPrev,
        propertyExpenseRatioMonth,
        propertyExpenseRatioYTD,
        propertyExpenseRatioPrev,
        badDebts,
        billings
    }
}

export default useExecutiveDashboardCalculations;
